import React, { useState, useEffect, useRef } from 'react';
import { 
  Palette, Plus, Trash2, Upload, Eye, EyeOff,
  Calendar, Loader2, Check, Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const MonthSelect = ({ value, onChange, small = false }) => (
  <Select value={String(value)} onValueChange={v => onChange(+v)}>
    <SelectTrigger className={small
      ? "bg-slate-900 border-blue-500/10 text-blue-200 text-xs h-7 w-[110px]"
      : "bg-slate-900 border-blue-500/20 text-white text-sm h-9 flex-1"
    }>
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="bg-slate-800 border-blue-500/20">
      {MONTHS.map((m, i) => (
        <SelectItem key={i} value={String(i + 1)} className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">
          {m}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const ThemeManager = ({ token, toast }) => {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newTheme, setNewTheme] = useState({
    name: '', start_month: 1, start_day: 1, end_month: 1, end_day: 31
  });
  const [creating, setCreating] = useState(false);
  const fileInputRefs = useRef({});

  useEffect(() => { loadThemes(); }, []);

  const loadThemes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/themes`, { headers: { Authorization: `Bearer ${token}` } });
      setThemes(res.data.themes || []);
    } catch (e) {
      console.error('Error loading themes:', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (slug, currentActive) => {
    setSaving(slug);
    try {
      await axios.put(`${API}/admin/themes/${slug}`, { is_active: !currentActive }, { headers: { Authorization: `Bearer ${token}` } });
      setThemes(prev => prev.map(t => t.slug === slug ? { ...t, is_active: !currentActive } : t));
      toast({ title: !currentActive ? 'Thème activé' : 'Thème désactivé' });
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const updateDates = async (slug, data) => {
    setSaving(slug);
    try {
      await axios.put(`${API}/admin/themes/${slug}`, data, { headers: { Authorization: `Bearer ${token}` } });
      setThemes(prev => prev.map(t => t.slug === slug ? { ...t, ...data } : t));
      toast({ title: 'Dates mises à jour' });
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const uploadImage = async (slug, file) => {
    setSaving(slug);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/themes/${slug}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setThemes(prev => prev.map(t => t.slug === slug ? { ...t, image_url: res.data.image_url } : t));
      toast({ title: 'Image mise à jour' });
    } catch (e) {
      toast({ title: 'Erreur upload', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const deleteTheme = async (slug) => {
    if (!window.confirm('Supprimer ce thème ?')) return;
    try {
      await axios.delete(`${API}/admin/themes/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
      setThemes(prev => prev.filter(t => t.slug !== slug));
      toast({ title: 'Thème supprimé' });
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const createTheme = async () => {
    if (!newTheme.name.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/themes`, newTheme, { headers: { Authorization: `Bearer ${token}` } });
      setThemes(prev => [...prev, res.data.theme]);
      setShowCreate(false);
      setNewTheme({ name: '', start_month: 1, start_day: 1, end_month: 1, end_day: 31 });
      toast({ title: 'Thème créé' });
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const getImageSrc = (theme) => {
    if (!theme.image_url) return null;
    if (theme.image_url.startsWith('/api/')) return `${BACKEND_URL}${theme.image_url}`;
    return theme.image_url;
  };

  const isCurrentlyActive = (theme) => {
    if (!theme.is_active) return false;
    const now = new Date();
    const m = now.getMonth() + 1;
    const d = now.getDate();
    const today = m * 100 + d;
    const start = theme.start_month * 100 + theme.start_day;
    const end = theme.end_month * 100 + theme.end_day;
    if (start <= end) return today >= start && today <= end;
    return today >= start || today <= end;
  };

  if (loading) {
    return <div className="text-center py-12"><Loader2 className="w-6 h-6 text-blue-400 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Palette className="w-6 h-6 text-purple-400" />
          <h2 className="text-xl font-bold text-white">Thèmes saisonniers</h2>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="create-theme-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau thème
        </Button>
      </div>

      <p className="text-sm text-blue-300/60">
        L'image de la boîte sur la page d'accueil change automatiquement selon la période configurée. Un seul thème est affiché à la fois (le premier correspondant à la date actuelle).
      </p>

      {/* Create form */}
      {showCreate && (
        <div className="bg-slate-800/50 border border-purple-500/20 rounded-xl p-5 space-y-4">
          <h3 className="text-white font-semibold">Nouveau thème</h3>
          <input
            type="text"
            placeholder="Nom du thème (ex: Pâques)"
            value={newTheme.name}
            onChange={e => setNewTheme(p => ({ ...p, name: e.target.value }))}
            className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/40 focus:outline-none focus:border-purple-400 text-sm"
            data-testid="new-theme-name"
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-blue-300/50 block mb-1">Début</label>
              <div className="flex gap-2 items-center">
                <MonthSelect value={newTheme.start_month} onChange={v => setNewTheme(p => ({ ...p, start_month: v }))} />
                <input type="number" min="1" max="31" value={newTheme.start_day} onChange={e => setNewTheme(p => ({ ...p, start_day: +e.target.value }))}
                  className="w-16 bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white text-sm text-center" />
              </div>
            </div>
            <div>
              <label className="text-xs text-blue-300/50 block mb-1">Fin</label>
              <div className="flex gap-2 items-center">
                <MonthSelect value={newTheme.end_month} onChange={v => setNewTheme(p => ({ ...p, end_month: v }))} />
                <input type="number" min="1" max="31" value={newTheme.end_day} onChange={e => setNewTheme(p => ({ ...p, end_day: +e.target.value }))}
                  className="w-16 bg-slate-900/50 border border-blue-500/20 rounded-lg px-3 py-2 text-white text-sm text-center" />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={createTheme} disabled={creating} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="submit-theme-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Créer
            </Button>
            <Button onClick={() => setShowCreate(false)} variant="outline" className="border-blue-500/30 text-blue-300">Annuler</Button>
          </div>
        </div>
      )}

      {/* Theme cards */}
      <div className="grid gap-4">
        {themes.map(theme => (
          <div key={theme.slug} className={`bg-slate-800/50 border rounded-xl p-5 transition-all ${isCurrentlyActive(theme) ? 'border-emerald-500/40 ring-1 ring-emerald-500/20' : theme.is_active ? 'border-blue-500/20' : 'border-slate-700/30 opacity-60'}`}
            data-testid={`theme-card-${theme.slug}`}>
            <div className="flex items-start gap-5">
              {/* Theme image preview */}
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-slate-900/50 border border-blue-500/10 flex-shrink-0 relative group">
                {getImageSrc(theme) ? (
                  <img src={getImageSrc(theme)} alt={theme.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-8 h-8 text-blue-300/30" />
                  </div>
                )}
                <button
                  onClick={() => fileInputRefs.current[theme.slug]?.click()}
                  className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <Upload className="w-5 h-5 text-white" />
                </button>
                <input
                  ref={el => fileInputRefs.current[theme.slug] = el}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => { if (e.target.files[0]) uploadImage(theme.slug, e.target.files[0]); }}
                />
              </div>

              {/* Theme info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-white">{theme.name}</h3>
                  {isCurrentlyActive(theme) && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium">
                      Actif maintenant
                    </span>
                  )}
                  {theme.is_builtin && (
                    <span className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300/50 text-xs">
                      Prédéfini
                    </span>
                  )}
                </div>

                {/* Date selectors */}
                <div className="flex items-center gap-3 text-sm mb-3 flex-wrap">
                  <Calendar className="w-4 h-4 text-blue-300/50" />
                  <MonthSelect small value={theme.start_month} onChange={v => updateDates(theme.slug, { start_month: v })} />
                  <input type="number" min="1" max="31" value={theme.start_day}
                    onChange={e => updateDates(theme.slug, { start_day: +e.target.value })}
                    className="w-12 bg-slate-900/50 border border-blue-500/10 rounded px-2 py-1 text-blue-200 text-xs text-center" />
                  <span className="text-blue-300/40">→</span>
                  <MonthSelect small value={theme.end_month} onChange={v => updateDates(theme.slug, { end_month: v })} />
                  <input type="number" min="1" max="31" value={theme.end_day}
                    onChange={e => updateDates(theme.slug, { end_day: +e.target.value })}
                    className="w-12 bg-slate-900/50 border border-blue-500/10 rounded px-2 py-1 text-blue-200 text-xs text-center" />
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                    onClick={() => toggleActive(theme.slug, theme.is_active)}
                    disabled={saving === theme.slug}
                    className={theme.is_active ? 'border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10' : 'border-slate-600 text-slate-400 hover:bg-slate-700'}
                    data-testid={`toggle-theme-${theme.slug}`}>
                    {saving === theme.slug ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : theme.is_active ? <Eye className="w-3 h-3 mr-1" /> : <EyeOff className="w-3 h-3 mr-1" />}
                    {theme.is_active ? 'Actif' : 'Inactif'}
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => fileInputRefs.current[theme.slug]?.click()}
                    className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10">
                    <Upload className="w-3 h-3 mr-1" /> Changer l'image
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => deleteTheme(theme.slug)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid={`delete-theme-${theme.slug}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {themes.length === 0 && (
        <div className="text-center py-12 text-blue-300/50">
          Aucun thème configuré. Les thèmes par défaut seront créés automatiquement.
        </div>
      )}
    </div>
  );
};

export default ThemeManager;
